import React, { useState, useEffect } from 'react';
import { contactService } from '../../services/contactService';
import DataTable from '../components/common/DataTable';
import InfoModal from '../components/common/InfoModal';
import DeleteConfirmation from '../components/common/DeleteConfirmation';
import ToastNotification from '../components/common/ToastNotification';

function ContactList() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  
  // Modal states
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await contactService.getAll();
      // Handle both response formats: { data: [] } or direct array
      const contactsData = Array.isArray(response?.data) ? response.data : 
                          Array.isArray(response) ? response : [];
      setContacts(contactsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const handleView = (contact) => {
    setSelectedContact(contact);
    setShowInfoModal(true);
  };

  const handleDeleteClick = (contact) => {
    setSelectedContact(contact);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await contactService.delete(selectedContact.id);
      showNotification('Contact deleted successfully');
      setShowDeleteModal(false);
      setSelectedContact(null);
      await fetchContacts();
    } catch (err) {
      showNotification(err.message, 'danger');
    }
  };

  const getSubjectBadgeVariant = (subject) => {
    if (!subject) return 'secondary';
    const subjectLower = subject.toLowerCase();
    if (subjectLower.includes('product') || subjectLower.includes('feedback')) return 'success';
    if (subjectLower.includes('support')) return 'info';
    if (subjectLower.includes('feature')) return 'warning';
    if (subjectLower.includes('bug')) return 'danger';
    return 'secondary';
  };

  const columns = [
    { 
      key: 'id', 
      header: 'ID',
      render: (value) => <span style={{ fontWeight: '500' }}>#{value}</span>
    },
    { 
      key: 'fullName', 
      header: 'Contact Information',
      render: (value, item) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontWeight: 'bold',
            color: 'white',
            fontSize: '16px'
          }}>
            {value?.charAt(0).toUpperCase() || '?'}
          </div>
          <div>
            <div style={{ fontWeight: '600' }}>{value}</div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>{item.email}</div>
            {item.phoneNumber && (
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                📞 {item.phoneNumber}
              </div>
            )}
          </div>
        </div>
      )
    },
    { 
      key: 'subject', 
      header: 'Subject',
      render: (value) => (
        <span className={`admin-crud-badge admin-crud-badge-${getSubjectBadgeVariant(value)}`}>
          {value || 'General'}
        </span>
      )
    },
    { 
      key: 'message', 
      header: 'Message Preview',
      render: (value) => (
        <div style={{ maxWidth: '250px' }}>
          <div style={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            color: '#475569'
          }}>
            {value}
          </div>
        </div>
      )
    },
    { 
      key: 'ipAddress', 
      header: 'IP Address',
      render: (value) => (
        <code style={{ fontSize: '12px', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
          {value || 'N/A'}
        </code>
      )
    },
    { 
      key: 'createdAt', 
      header: 'Submitted',
      render: (value) => (
        <div style={{ fontSize: '12px' }}>
          {value ? new Date(value).toLocaleDateString() : 'N/A'}
          {value && (
            <div style={{ fontSize: '10px', color: '#94a3b8' }}>
              {new Date(value).toLocaleTimeString()}
            </div>
          )}
        </div>
      )
    }
  ];

  const infoFields = [
    { key: 'id', label: 'Contact ID' },
    { key: 'fullName', label: 'Full Name' },
    { key: 'email', label: 'Email Address' },
    { key: 'phoneNumber', label: 'Phone Number', render: (value) => value || 'Not provided' },
    { 
      key: 'subject', 
      label: 'Subject',
      render: (value) => value || 'General Inquiry',
      badge: (value) => getSubjectBadgeVariant(value)
    },
    { 
      key: 'message', 
      label: 'Message',
      render: (value) => (
        <div style={{ 
          background: '#f8fafc', 
          padding: '12px', 
          borderRadius: '8px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {value}
        </div>
      )
    },
    { key: 'ipAddress', label: 'IP Address', render: (value) => value || 'N/A' },
    { 
      key: 'createdAt', 
      label: 'Submitted Date',
      render: (value) => value ? new Date(value).toLocaleString() : 'N/A'
    },
    { 
      key: 'userAgent', 
      label: 'Browser/Device',
      render: (value) => value ? (
        <div style={{ fontSize: '12px', wordBreak: 'break-word' }}>
          {value}
        </div>
      ) : 'N/A'
    }
  ];

  if (loading) {
    return (
      <div className="admin-crud-wrapper">
        <div className="admin-crud-loader">
          <div className="admin-crud-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-crud-wrapper">
      <div className="admin-crud-container">
        {error && (
          <div className="admin-crud-toast" style={{ position: 'relative', marginBottom: '20px' }}>
            <div className="admin-crud-toast-content admin-crud-toast-danger">
              <div className="admin-crud-toast-header">
                <strong>Error</strong>
                <button onClick={() => setError(null)}>✕</button>
              </div>
              <div className="admin-crud-toast-body">{error}</div>
            </div>
          </div>
        )}

        <DataTable
          title="Contact Messages"
          columns={columns}
          data={contacts}
          onView={handleView}
          onDelete={handleDeleteClick}
          searchPlaceholder="Search by name, email, subject or message..."
          addButtonText="No Create" // This will hide the add button
          hideAddButton={true} // Add this prop to hide add button
        />

        {/* Info Modal */}
        <InfoModal
          show={showInfoModal}
          onHide={() => {
            setShowInfoModal(false);
            setSelectedContact(null);
          }}
          title="Contact Message Details"
          data={selectedContact}
          fields={infoFields}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmation
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedContact(null);
          }}
          onConfirm={handleConfirmDelete}
          title="Delete Contact Message"
          message={`Are you sure you want to delete this message from "${selectedContact?.fullName}"? This action cannot be undone.`}
        />

        {/* Toast Notification */}
        <ToastNotification
          show={showToast}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          type={toastType}
        />
      </div>
    </div>
  );
}

export default ContactList;